import { fitText } from "@/lib/fit-title";
import { QaDeepDive } from "@/components/qa/QaDeepDive";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { absoluteUrl } from "@/config/sites";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { WP_REVIEWED_ISO, WP_REVIEWED_TH } from "@/data/workpermit";
import { WP_QA_ALL, WP_QA_BY_SLUG } from "@/data/workpermit/questions";
import { JsonLd } from "@/components/JsonLd";

export const Route = createFileRoute("/work-permit/q/$slug")({
  loader: ({ params }) => {
    if (!WP_QA_BY_SLUG[params.slug]) throw notFound();
    return { slug: params.slug };
  },
  head: ({ loaderData }) => {
    const qa = loaderData ? WP_QA_BY_SLUG[loaderData.slug] : undefined;
    if (!qa) {
      return { meta: [{ title: ctrTitle("ไม่พบหน้า") }, { name: "robots", content: "noindex" }] };
    }
    const url = absoluteUrl(`/work-permit/q/${qa.slug}`);
    const title = fitText(`${qa.q} | Thai Notary Law`, 60);
    return {
      meta: [
        { title: ctrTitle(title) },
        { name: "description", content: ctrDesc(fitText(qa.a, 158)) },
        { property: "og:title", content: ctrTitle(title) },
        { property: "og:description", content: ctrDesc(fitText(qa.a, 158)) },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl p-8 text-center">
      <h1 className="mb-2 text-xl font-bold">ไม่พบคำถามนี้</h1>
      <Link to="/work-permit/questions" className="text-blue-700 hover:underline">
        กลับไปหน้ารวมคำถาม →
      </Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-2xl p-8 text-center text-sm">
      โหลดหน้านี้ไม่สำเร็จ กรุณารีเฟรชอีกครั้ง
    </div>
  ),
  component: WpQAPage,
});

function WpQAPage() {
  const { slug } = Route.useLoaderData();
  const qa = WP_QA_BY_SLUG[slug];
  if (!qa) throw notFound();
  const url = absoluteUrl(`/work-permit/q/${qa.slug}`);
  const related = WP_QA_ALL.filter(
    (x) => x.slug !== qa.slug && qa.topicSlug && x.topicSlug === qa.topicSlug,
  ).slice(0, 8);

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        url,
        dateModified: WP_REVIEWED_ISO,
        mainEntity: [
          { "@type": "Question", name: qa.q, acceptedAnswer: { "@type": "Answer", text: qa.a } },
        ],
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [".answer-summary", ".faq-answer"],
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าแรก", item: absoluteUrl("/") },
          {
            "@type": "ListItem",
            position: 2,
            name: "ใบอนุญาตทำงานและงานตรวจคนเข้าเมือง",
            item: absoluteUrl("/work-permit"),
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "คำถามที่พบบ่อย",
            item: absoluteUrl("/work-permit/questions"),
          },
          { "@type": "ListItem", position: 4, name: qa.q, item: url },
        ],
      },
    ],
  };

  return (
    <>
      <JsonLd data={graph} />
      <Breadcrumbs
        items={[
          { label: "หน้าแรก", to: "/" },
          { label: "ใบอนุญาตทำงานและงานตรวจคนเข้าเมือง", to: "/work-permit" },
          { label: "คำถามที่พบบ่อย", to: "/work-permit/questions" },
          { label: qa.q },
        ]}
      />
      <section className="mx-auto max-w-3xl px-4 py-10">
        <h1 className="mb-4 text-2xl font-bold">{qa.q}</h1>
        <p className="answer-summary faq-answer mb-6 text-base leading-relaxed" data-speakable>
          {qa.a}
        </p>
        <QaDeepDive path={`/work-permit/q/${qa.slug}`} />
        <p className="mb-6 text-xs text-muted-foreground">
          คำค้นที่เกี่ยวข้อง: {qa.synonyms.join(" · ")} · ข้อมูล ณ {WP_REVIEWED_TH}
        </p>
        {related.length > 0 && (
          <>
            <h2 className="mb-2 text-lg font-semibold">คำถามที่เกี่ยวข้อง</h2>
            <ul className="mb-6 list-disc space-y-1 pl-5 text-sm">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to="/work-permit/q/$slug"
                    params={{ slug: r.slug }}
                    className="text-blue-700 hover:underline"
                  >
                    {r.q}
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="text-sm">
          <Link to="/work-permit" className="text-blue-700 hover:underline">
            ภาพรวมบริการ →
          </Link>{" "}
          ·{" "}
          <Link to="/contact" className="text-blue-700 hover:underline">
            สอบถามเจ้าหน้าที่ (โทร / LINE / อีเมล) →
          </Link>
        </p>
      </section>
    </>
  );
}
